import { createClient, type ApiClient } from "@majourneys/api-client";
import { apiBaseAbsolute } from "./api-base";

/**
 * Adds the visitor's address to calls made DURING A SERVER RENDER, alongside a
 * shared secret so the API will believe it.
 *
 * Without it every server-rendered call reaches the API from the worker's own
 * address, so the API's per-caller spend guard counts the entire site as one
 * visitor. Measured 2026-08-22: 1,921 of ~2,020 API requests in half an hour
 * came from a single worker address.
 *
 * Browser calls are untouched: they already arrive with the guest's real
 * address, and shipping the secret to a browser would hand anyone the ability
 * to claim any address they liked.
 */
function ssrIdentityHeaders(): Record<string, string> {
  if (typeof window !== "undefined") return {};
  /**
   * Read off `globalThis`, never imported. The context module pulls in
   * `node:async_hooks`, and importing it here put AsyncLocalStorage into the
   * browser bundle — this file ships to guests.
   */
  const read = (globalThis as Record<string, unknown>)["__mjSsrRequestContext"] as
    | (() => { clientIp: string | null; ssrKey: string | null } | null)
    | undefined;
  const context = read?.();
  if (!context?.clientIp || !context.ssrKey) return {};
  return {
    "x-mj-client-ip": context.clientIp,
    "x-mj-ssr-key": context.ssrKey,
  };
}

const credentialedFetch = ((input: RequestInfo | URL, init?: RequestInit) =>
  fetch(input, {
    ...init,
    // A call may opt out (see staysApi.property's `anonymous`); everything
    // else carries the session cookie as before.
    credentials: init?.credentials ?? "include",
    headers: { ...(init?.headers ?? {}), ...ssrIdentityHeaders() },
  })) as typeof fetch;

let browserClient: ApiClient | null = null;

export function createBrowserClient(): ApiClient {
  if (browserClient) return browserClient;
  const baseUrl = apiBaseAbsolute();
  browserClient = createClient(baseUrl, { fetcher: credentialedFetch });
  return browserClient;
}

export function createServerClient(baseUrl: string): ApiClient {
  return createClient(baseUrl, { fetcher: credentialedFetch });
}

export const api: ApiClient = createBrowserClient();
